
import { Component, Vue, Watch } from "vue-property-decorator"
import { Getter, Action } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import AccountSetup from "@/components/Login/AccountSetup.vue"
import UserProfilePicture from "@/components/Account/UserProfilePicture.vue"

import api from "@/services/api"
import User, { CurrentUser } from "../types/user"
import { Case } from "@/types"
import { UserStatus } from "@/types/userStatus"
import vSelect from "vue-select"
import PageWrapper from "@/components/New/Page/PageWrapper.vue"

@Component({
	components: {
		"page": PageWrapper,
		"skeleton": SkeletonLoader,
		"scrollbar": VuePerfectScrollbar,
		"v-select": vSelect,
		"account-setup": AccountSetup,
		"user-profile-picture": UserProfilePicture
	}
})
export default class UserProfile extends Vue {
	@Getter("getMe") public me!: CurrentUser;

	@Getter("getAvailableUserStatuses") public availableUserStatuses!: UserStatus[];

	@Action("setCurrentUserStatus")
	private setCurrentUserStatus!: (userStatus: UserStatus) => Promise<void>;

	@Action("clearCurrentUserStatus")
	private clearCurrentUserStatus!: () => Promise<void>;

	@Action("loadAvailableUserStatuses")
	private loadAvailableUserStatuses!: () => Promise<void>;

	private userDetails: User | null = null;
	private activeUserCases: Case[] | null = null;
	private userCases: Case[] | null = null;

	private pageCount: number = 0;
	private resultsCount: number = 0;
	private pageNumber: number = 1;
	private perPage: number = 50;

	private caseSearchOptions: any[] = [
		{ text: "Assigned", value: false },
		{ text: "Collaborating", value: true }
	];
	private caseSearchOption = false;

	/**
	 * Current user ID from params
	 */
	public get userId (): string {
		return this.$route.params.userId
	}

	/**
	 * Columns for cases tables
	 */
	public get casesColumns (): any[] {
		return [
			{
				key: "caseNumber",
				label: "Case #"
			},
			{
				key: "title"
			},
			{
				key: "templateTitle",
				label: "Template"
			},
			{
				key: "areas"
			}
		]
	}

	/**
	 * List of active cases - filtered to assignee if caseSearchOption is false
	 */
	public get activeCases (): Case[] | null {
		if (this.caseSearchOption || !this.activeUserCases) {
			return this.activeUserCases
		}

		return this.activeUserCases!.filter(activeCase => activeCase.assigneeId === this.userId)
	}

	private get isMe (): boolean {
		return this.me && this.userId === this.me.id
	}

	/**
	 * On user ID change, update user
	 */
	@Watch("userId")
	public onUserIdChanged () {
		this.userDetails = null
		if (this.userId) {
			this.loadUser()
		}
	}

	/**
	 * On mount, get user details
	 */
	public async mounted () {
		if (this.userId) {
			await this.loadUser()
		}

		this.loadAvailableUserStatuses()
	}

	/**
	 * Fetch user details for current user ID
	 */
	public async loadUser () {
		this.userDetails = await api.userDetails(this.userId)
		this.search()
	}

	/**
	 * Go to case
	 */
	public gotoCase (caseToGoTo: Case): void {
		this.$router.push({ path: `/cases/${caseToGoTo.caseNumber}` })
	}

	public search () {
		this.pageNumber = 1

		api.userCases(this.userId, this.caseSearchOption, false, 1)
			.then(records => this.activeUserCases = records.data)
		api.userCases(this.userId, this.caseSearchOption, true, 1)
			.then(records => {
				this.userCases = records.data
				this.pageCount = records.pageCount
				this.resultsCount = records.totalItems
			})
	}

	public get userHasSetStatusPermission () : boolean {
		return !!(this.me &&
			this.me.extendedPermissions &&
			this.me.extendedPermissions["setOtherUsersStatus"])
	}

	private async userStatusChanged (userId: string, userStatus: UserStatus) {
		if (this.userDetails) {
			this.userDetails.currentStatus = userStatus
		}

		if (userId === this.me.id) {
			if (userStatus) {
				await this.setCurrentUserStatus(userStatus)
			} else {
				await this.clearCurrentUserStatus()
			}
			return
		}

		if (userStatus) {
			await api.setUserStatus(userId, userStatus.id!)
		} else {
			await api.clearUserStatus(userId)
		}
	}

	/**
	 * @summary Handler which clears the picture URL in our user details.
	 */
	private onUserPictureUpdated () {
		this.userDetails!.pictureUrl = null
	}
}
